/**
 * 048 T025 / T045 / T047 — the workflow list.
 *
 * Three things live on this page, in this order, because that is the order an admin needs them:
 * whether the queue is healthy, what automations exist, and what has broken. A list of workflows
 * with no queue health is how 028 shipped, and a tenant's booking confirmations were dead for days
 * with nothing in the product recording it.
 */
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Pause, Play, Trash2, Workflow as WorkflowIcon, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { AdminOnly } from '@/components/admin-only'
import {
  Page, PageHeader, Card, CardHeader, btnPrimary, btnSecondary, btnGhost,
  hoverActions, StatusPill, EmptyState, SkeletonRows, useConfirm,
} from '@/components/rm'
import { Modal } from '@/components/rm/form'
import {
  useWorkflows, useWorkflowHealth, useSetWorkflowEnabled, useDeleteWorkflow,
  usePublishWorkflow, useEventTypes,
} from '@/hooks/use-workflows'
import { useCompany } from '@/hooks/use-company'
import { WORKFLOW_TEMPLATES, instantiate } from '@/lib/workflow-templates'

export const Route = createFileRoute('/_app/workflows')({
  component: () => <AdminOnly><WorkflowsPage /></AdminOnly>,
})

const ago = (iso: string | null) => {
  if (!iso) return '—'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h} h ago`
  return `${Math.round(h / 24)} d ago`
}

function WorkflowsPage() {
  const nav = useNavigate()
  const { data: rows, isLoading } = useWorkflows()
  const { company } = useCompany()
  const health = useWorkflowHealth(company?.workflow_daily_run_cap ?? 2000)
  const events = useEventTypes()
  const setEnabled = useSetWorkflowEnabled()
  const remove = useDeleteWorkflow()
  const publish = usePublishWorkflow()
  const [confirm, confirmDialog] = useConfirm()
  const [picking, setPicking] = useState(false)

  const eventLabel = (t: string | null) =>
    !t ? 'On a schedule' : (events.data ?? []).find((e) => e.event_type === t)?.description ?? t

  const install = async (key: string) => {
    const t = WORKFLOW_TEMPLATES.find((x) => x.key === key)
    if (!t) return
    // Installed paused. A template with an empty template_id or form_id would otherwise start
    // sending the moment it is created, which is not what "have a look at this one" means.
    const r = await publish.mutateAsync({
      name: t.name,
      trigger_event_type: t.trigger_event_type,
      graph: instantiate(t),
      enabled: false,
    })
    if (r.errors?.length || r.error) {
      toast.error(r.error ?? r.errors![0].message)
      return
    }
    setPicking(false)
    toast.success('Installed, paused. Fill in the blanks, then turn it on.')
    if (r.workflow_id) nav({ to: '/workflows/$id', params: { id: r.workflow_id } })
  }

  const capPct = health.data ? Math.round((health.data.runsToday / Math.max(1, health.data.cap)) * 100) : 0
  const late = health.data?.oldestPendingMinutes ?? null
  const trouble = (late != null && late > 10) || (health.data?.failures24h ?? 0) > 0 || capPct >= 80

  return (
    <Page>
      <PageHeader
        eyebrow="Automation"
        title="Workflows"
        subtitle="Draw what should happen after an event, and the product does it every time."
        actions={
          <>
            <button type="button" className={btnSecondary} onClick={() => setPicking(true)}>
              Start from a template
            </button>
            <button type="button" className={btnPrimary} onClick={() => nav({ to: '/workflows/$id', params: { id: 'new' } })}>
              <Plus className="h-4 w-4" /> New workflow
            </button>
          </>
        }
      />

      {/* Queue health. Always shown, not only when broken: a panel that appears only on failure
          teaches nobody what normal looks like. */}
      <Card className="mt-5">
        <CardHeader title="Queue health" />
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-b-[12px] bg-token-border sm:grid-cols-3">
          <Stat
            label="Oldest step waiting"
            value={late == null ? 'Nothing late' : `${late} min`}
            tone={late != null && late > 10 ? 'danger' : 'ok'}
          />
          <Stat
            label="Failures, last 24 h"
            value={String(health.data?.failures24h ?? 0)}
            tone={(health.data?.failures24h ?? 0) > 0 ? 'danger' : 'ok'}
          />
          <Stat
            label="Runs today"
            value={`${health.data?.runsToday ?? 0} of ${health.data?.cap ?? 2000}`}
            tone={capPct >= 100 ? 'danger' : capPct >= 80 ? 'warn' : 'ok'}
          />
        </div>
        {trouble && (
          <p className="flex items-start gap-2 border-t border-token px-4 py-3 text-[12.5px] text-token-secondary">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-token-warning" aria-hidden />
            <span>
              {capPct >= 100
                ? 'Today’s run cap is reached. New events are recorded but will not start a run until tomorrow.'
                : capPct >= 80
                  ? 'Close to today’s run cap.'
                  : late != null && late > 10
                    ? 'Steps are running late. The dispatcher may not be firing.'
                    : 'A step failed in the last day. Open its workflow to see which.'}
            </span>
          </p>
        )}
      </Card>

      <Card className="mt-5">
        <CardHeader title="Your workflows" count={rows?.length} />
        {isLoading ? (
          <SkeletonRows count={3} />
        ) : !rows?.length ? (
          <EmptyState
            label="No workflows yet. A template is the quickest way to see what one looks like."
            cta="Start from a template"
            onCta={() => setPicking(true)}
          />
        ) : (
          <ul className="divide-y divide-token">
            {rows.map((w) => (
              <li
                key={w.id}
                className="group flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-token-hover"
                onClick={() => nav({ to: '/workflows/$id', params: { id: w.id } })}
              >
                <WorkflowIcon className="h-4 w-4 shrink-0 text-token-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-token-primary">{w.name}</p>
                  <p className="truncate text-[12px] text-token-secondary">
                    {eventLabel(w.trigger_event_type)}
                    {w.current_version > 0 && ` · version ${w.current_version}`}
                    {` · created ${ago(w.created_at)}`}
                  </p>
                </div>
                <StatusPill
                  status={w.enabled ? 'active' : 'draft'}
                  label={w.enabled ? 'On' : w.current_version === 0 ? 'Not published' : 'Paused'}
                />
                <div className={`flex items-center gap-1 ${hoverActions}`} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button" className={btnGhost}
                    aria-label={w.enabled ? 'Pause this workflow' : 'Turn this workflow on'}
                    disabled={w.current_version === 0}
                    onClick={() => setEnabled.mutate({ id: w.id, enabled: !w.enabled },
                      { onSuccess: () => toast.success(w.enabled ? 'Paused. Runs already going will finish.' : 'On.') })}
                  >
                    {w.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button
                    type="button" className={btnGhost} aria-label="Delete this workflow"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Delete this workflow?',
                        body: 'It stops running now. Its history stays, so past runs can still be read.',
                        confirmLabel: 'Delete',
                        tone: 'danger',
                      })
                      if (ok) remove.mutate(w.id, { onSuccess: () => toast.success('Deleted') })
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal open={picking} title="Start from a template" onClose={() => setPicking(false)}>
          <ul className="space-y-2">
            {WORKFLOW_TEMPLATES.map((t) => (
              <li key={t.key}>
                <button
                  type="button"
                  className="w-full rounded-[12px] border border-token p-3.5 text-left transition-colors hover:bg-token-hover disabled:opacity-50"
                  disabled={publish.isPending}
                  onClick={() => install(t.key)}
                >
                  <p className="text-[14px] font-semibold text-token-primary">{t.name}</p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-token-secondary">{t.summary}</p>
                </button>
              </li>
            ))}
          </ul>
        <p className="mt-3 text-[12px] text-token-secondary">
          Every template installs paused so you can fill in the blanks first.
        </p>
      </Modal>

      {confirmDialog}
    </Page>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'warn' | 'danger' }) {
  const colour = tone === 'danger' ? 'text-token-danger' : tone === 'warn' ? 'text-token-warning' : 'text-token-primary'
  return (
    <div className="bg-token-surface px-4 py-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">{label}</p>
      <p className={`font-display mt-1 text-[18px] font-semibold ${colour}`}>{value}</p>
    </div>
  )
}
